import styled, { css } from 'styled-components'
import { Devices, Spacings } from '@styles'
import TotalAmount from './ComponentUI/Sales/TotalAmount'
import InvoicedTarget from './ComponentUI/Sales/InvoicedTarget'
import EnquiryPaymentOrder from './ComponentUI/Sales/EnquiryPaymentOrder'
import Deeplinks from './ComponentUI/Deeplinks'
import OpenPurchaseOrders from './ComponentUI/Sales/OpenPurchaseOrders'
import RecentlyOnboarded from './ComponentUI/Sales/RecentlyOnboarded'
import RecentUpdates from './ComponentUI/Sales/RecentUpdates'
import EnquiryVsOrderChart from './ComponentUI/Sales/EnquiryVsOrderChart'
import ThreeDBarChart from './ComponentUI/Sales/MonthlyPerformance'

import AddOrganisation from '@assets/dashboard/add-organisation.svg'
import AssignedLeads from '@assets/dashboard/assigned-leads.svg'
import BuyerQuotePending from '@assets/dashboard/buyer-quote-pending.svg'
import DelayedOrders from '@assets/dashboard/delayed-orders.svg'
import RequestForCreditIcon from '@assets/dashboard/request-for-credit.svg'
import SupplyQuotePending from '@assets/dashboard/supply-quote-pending.svg'
import { paths } from '@routes'
import { strings } from '@constants'
import { useQuery } from '@hooks'
import TopOrganisations from './ComponentUI/Sales/TopOrganisations'

export const ContainerWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding: ${Spacings.SPACING_4B} 0rem;
`
export const Wapper = styled.div`
	width: 100%;
	display: flex;
	gap: ${Spacings.SPACING_4B};
	flex-direction: column;
	@media ${Devices.mobile} {
		flex-direction: column;
	}
	@media ${Devices.tablet} {
		flex-direction: column;
	}
	@media ${Devices.desktop} {
		flex-direction: row;
		${({ setFullRow }) => {
			if (setFullRow) {
				return css`
					flex-direction: column;
				`
			}
		}}
	}
	@media ${Devices.laptop} {
		flex-direction: row;
		${({ setFullRow }) => {
			if (setFullRow) {
				return css`
					flex-direction: column;
				`
			}
		}}
	}
`

// Month short names
export const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DeeplinksData = [
	{
		title: strings('add', 'organisation'),
		icon: AddOrganisation,
		href: `${paths.admin_organisations}?add_organisation=true`,
		actionButton: false,
	},
	{ title: strings('assigned', 'leads'), icon: AssignedLeads, href: `${paths.admin_lead_all}?add_lead=true`, actionButton: false },
	{ title: strings(['buyer', 'quote', 'pending']), icon: BuyerQuotePending, href: paths.admin_manage_enquiry_active, actionButton: false },
	{ title: strings('pending', 'orders'), icon: DelayedOrders, href: paths.admin_manage_enquiry_orders_order_book, actionButton: false },
	{ title: strings('request_credit'), icon: RequestForCreditIcon, href: '#', actionButton: true },
	{
		title: strings(['supply', 'quote', 'pending']),
		icon: SupplyQuotePending,
		href: `${paths.admin_manage_enquiry_active}?has_quotes_filter=false`,
		actionButton: false,
	},
]

const toFirstOfMonth = (dateStr) => {
	if (!dateStr) return null
	const d = new Date(dateStr)
	if (isNaN(d.getTime())) return null
	return new Date(d.getFullYear(), d.getMonth(), 1).getTime()
}

const toLastOfMonth = (dateStr) => {
	if (!dateStr) return null
	const d = new Date(dateStr)
	if (isNaN(d.getTime())) return null
	return new Date(d.getFullYear(), d.getMonth() + 1, 0).getTime()
}

const SalesDashboard = ({ data, dashboardType, hasOrganisationId, organisationName, employeeName, fromDate, toDate }) => {
	const query = useQuery()
	const setFullRow = query?.get('numMonths') ? ['0', '12', 'OVERALL'].includes(query?.get('numMonths')) : null
	const from = toFirstOfMonth(+fromDate)
	const to = toLastOfMonth(+toDate)
	return (
		<>
			<ContainerWrapper>
				<Wapper>
					<TotalAmount
						total_amount={data?.total_amount ?? 0}
						over_due_amount={data?.over_due_amount ?? 0}
						due_amount={data?.due_amount ?? 0}
						organisationName={organisationName}
						employeeName={employeeName}
						fromDate={from}
						toDate={to}
					/>
					<InvoicedTarget
						dashboardType={dashboardType}
						hasOrganisationId={hasOrganisationId}
						total_sales_business_amount={data?.total_sales_business?.amount ?? '-'}
						credit_amount={data?.total_sales_business?.credit_amount ?? '-'}
						monthly_target={data?.monthly_target ?? '-'}
						gross_margin={data?.gross_margin ?? '-'}
						yesterdays_invoice_amount={data?.yesterdays_invoice_amount ?? '-'}
						organisationName={organisationName}
						employeeName={employeeName}
						fromDate={from}
						toDate={to}
					/>
				</Wapper>
				<Wapper>
					<EnquiryPaymentOrder
						EnquiryData={data?.total_assigned_weight || []} //Enquiry
						NewUserOnboarded={data?.onboarded_org_count ?? '-'} //New User Onboarded
						QuotesGiven={data?.total_quoted_weight || []} //Quotes Given
						ReceivedYesterday={data?.payment_received_yesterday ?? '-'} //Received yesterday
						ExpectedDays={data?.expected_payment ?? '-'} //Expected (Next n days)
						TotalOrders={data?.total_ordered_weight ?? '-'} //Total orders
						OrderedWeight={data?.ordered_weight || []}
						OrderBook={data?.pending_order} //Order book (pending)
						ServicedMonth={data?.total_serviced_weight} //Serviced (This month)
						ServicedWeight={data?.serviced_weight || []}
						TotalPendingOrder={data?.total_pending_order || []}
					/>
					<Deeplinks DeeplinksData={DeeplinksData} />
				</Wapper>
				<Wapper setFullRow={setFullRow}>
					<EnquiryVsOrderChart EnquiryData={data?.assigned_enquiries} OrderData={data?.total_orders} />
					<ThreeDBarChart MonthlyPerformanceData={data?.monthly_sales_data} />
				</Wapper>
				<Wapper>
					<OpenPurchaseOrders
						open_purchase_order_delayed={data?.open_purchase_order_delayed}
						open_purchase_order_running={data?.open_purchase_order_running}
						open_purchase_count={data?.total_open_purchase_count || 0}
						running={data?.total_running_purchase_count || 0}
						delayed={data?.total_delayed_purchase_count || 0}
					/>
				</Wapper>
				{/* <Wapper>
				<ReceivablesInvoice />
				</Wapper> */}
				<Wapper>
					<RecentlyOnboarded organisation_onboarded={data?.organisation_onboarded} />
					<TopOrganisations top_organisations={data?.organisation_details} />
				</Wapper>
				<Wapper>
					<RecentUpdates enquiries={data?.enquiries} payments={data?.payments} invoices={data?.invoices} />
				</Wapper>
			</ContainerWrapper>
		</>
	)
}

export default SalesDashboard
